import { parseMarkdown } from "@wiki/core";
import { dropCursor } from "prosemirror-dropcursor";
import { gapCursor } from "prosemirror-gapcursor";
import { history } from "prosemirror-history";
import { Node as ProseMirrorNode } from "prosemirror-model";
import { EditorState, type Plugin } from "prosemirror-state";
import { columnResizing, tableEditing } from "prosemirror-tables";

import { collaborationPlugins, type CollaborationOptions } from "./collab";
import type { EditorContext } from "./context";
import { buildInputRules, inputRulesPlugin } from "./input-rules";
import { buildKeymap } from "./keymap";
import { pastePlugin } from "./paste";
import { commentGutterPlugin, commentsPlugin } from "./plugins/comments";
import {
  editorAttributesPlugin,
  maxLengthPlugin,
  placeholderPlugin,
  trailingParagraphPlugin,
  withTrailingParagraph,
} from "./plugins/document-plugins";
import { findPlugin } from "./plugins/find-replace";
import { headingsPlugin } from "./plugins/headings";
import { linkToolbarPlugin } from "./plugins/link-toolbar";
import { suggestionPlugin, type SuggestionBridge } from "./plugins/suggestions";
import { uploadPlaceholderPlugin } from "./plugins/upload";
import { schema } from "./schema";
import { buildSmartTextRules } from "./smart-text";
import type { EditorValue } from "./types";

export interface CreateEditorStateOptions {
  value?: EditorValue;
  context: EditorContext;
  bridge: SuggestionBridge;
  collaboration?: CollaborationOptions;
  /** Injectable for tests; the page's own origin by default. */
  origin?: string;
}

/**
 * `value` may be markdown, ProseMirror JSON, or an already-built node. Stored
 * content gets the trailing-paragraph invariant applied once here, on load, so
 * that opening a document does not itself register as an edit (§16).
 */
export function docFromValue(value: EditorValue | undefined): ProseMirrorNode {
  if (value === undefined || value === "") {
    const empty = schema.topNodeType.createAndFill();
    if (empty === null) throw new Error("The schema cannot build an empty document");
    return withTrailingParagraph(empty);
  }
  if (typeof value === "string") return withTrailingParagraph(parseMarkdown(value));
  if (value instanceof ProseMirrorNode) return withTrailingParagraph(value);
  return withTrailingParagraph(ProseMirrorNode.fromJSON(schema, value));
}

export function editorPlugins(options: CreateEditorStateOptions): Plugin[] {
  const { context } = options;
  const collaborative = options.collaboration !== undefined;
  return [
    // The suggestion menus take the arrow keys and Enter while open, so they
    // sit ahead of every keymap.
    suggestionPlugin(context, options.bridge),
    inputRulesPlugin(buildSmartTextRules()),
    ...buildKeymap({ context, collaborative }),
    ...(collaborative && options.collaboration
      ? collaborationPlugins(options.collaboration)
      : [history()]),
    columnResizing(),
    tableEditing(),
    dropCursor({ color: "var(--app-accent)", width: 2 }),
    gapCursor(),
    pastePlugin({ context, ...(options.origin === undefined ? {} : { origin: options.origin }) }),
    uploadPlaceholderPlugin(),
    commentsPlugin(context),
    commentGutterPlugin(context),
    headingsPlugin(),
    findPlugin(),
    linkToolbarPlugin(),
    placeholderPlugin(context),
    trailingParagraphPlugin(context),
    maxLengthPlugin(context),
    editorAttributesPlugin(context),
  ];
}

export function createEditorState(options: CreateEditorStateOptions): EditorState {
  const plugins = editorPlugins(options);
  // Under collaboration the document comes from the Yjs fragment, so the
  // state starts empty and `ySyncPlugin` fills it.
  if (options.collaboration) return EditorState.create({ schema, plugins });
  return EditorState.create({ doc: docFromValue(options.value), plugins });
}

export { buildInputRules };
